import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useGetCustomersQuery, useGetCustomerQuery } from '../../api/customerApi';
import type { Customer, CustomerListParams } from '../../types/customer';
import { CreateCustomerModal } from './CreateCustomerModal';
import { CustomerDetail } from './CustomerDetail';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const CreateButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;

  &:hover {
    background: #3182ce;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  min-width: 200px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
`;

const TableContainer = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f7fafc;
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0;
`;

const TableRow = styled.tr<{ clickable?: boolean }>`
  &:hover {
    background: #f7fafc;
    ${({ clickable }) => clickable && `cursor: pointer;`}
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
  }
  
  ${({ clickable }) => clickable && `
    transition: background-color 0.15s;
  `}
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: #2d3748;
  font-size: 14px;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  ${({ status }) => {
    switch (status) {
      case 'active':
        return 'background: #c6f6d5; color: #22543d;';
      case 'inactive':
        return 'background: #fed7d7; color: #742a2a;';
      case 'pending':
        return 'background: #fef5e7; color: #744210;';
      case 'canceled':
        return 'background: #e2e8f0; color: #4a5568;';
      default:
        return 'background: #e2e8f0; color: #4a5568;';
    }
  }}
`;

const PlanBadge = styled.span<{ plan: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  ${({ plan }) => {
    switch (plan) {
      case 'pro':
        return 'background: #bee3f8; color: #2a4365;';
      case 'starter':
        return 'background: #d6f5d6; color: #22543d;';
      default:
        return 'background: #e2e8f0; color: #4a5568;';
    }
  }}
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #718096;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #e53e3e;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 16px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  gap: 16px;
  flex-wrap: wrap;
`;

const PaginationInfo = styled.div`
  color: #718096;
  font-size: 14px;
  flex: 1;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: #f7fafc;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #718096;
  text-align: center;
`;

export const CustomerList: React.FC = () => {
  const [params, setParams] = useState<CustomerListParams>({
    page: 1,
    limit: 20,
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const { data, error, isLoading, refetch } = useGetCustomersQuery(params);
  const { data: customerDetailData } = useGetCustomerQuery(selectedCustomerId!, {
    skip: !selectedCustomerId,
  });

  const handleSearchChange = (search: string) => {
    setParams(prev => ({ ...prev, search: search || undefined, page: 1 }));
  };

  const handlePlanFilter = (plan: string) => {
    setParams(prev => ({
      ...prev,
      plan: plan === 'all' ? undefined : plan as 'starter' | 'pro',
      page: 1
    }));
  };

  const handleStatusFilter = (status: string) => {
    setParams(prev => ({
      ...prev,
      subscriptionStatus: status === 'all' ? undefined : status as any,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setParams(prev => ({ ...prev, page }));
  };

  const handleCreateSuccess = () => {
    refetch(); // Refresh the customer list
    setIsCreateModalOpen(false);
  };

  const openDetail = (customer: Customer) => setSelectedCustomerId(customer.id);
  const closeDetail = () => setSelectedCustomerId(null);

  if (isLoading) {
    return (
      <Container>
        <LoadingState>Loading customers...</LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorState>
          <div>Failed to load customers</div>
          <div>Please try again later</div>
        </ErrorState>
      </Container>
    );
  }

  const customers = data?.data.customers || [];
  const pagination = data?.data.pagination;

  // Calculate stats
  const totalCustomers = pagination?.totalItems || 0;
  const activeCustomers = customers.filter(c => c.subscriptionStatus === 'active').length;
  const proCustomers = customers.filter(c => c.plan === 'pro').length;
  const starterCustomers = customers.filter(c => c.plan === 'starter').length;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>Customers</Title>
          <CreateButton onClick={() => setIsCreateModalOpen(true)}>
            <span>+</span>
            Create Customer
          </CreateButton>
        </HeaderLeft>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search customers..."
            onChange={(e) => handleSearchChange(e.target.value)}
            value={params.search || ''}
          />
          <Select
            value={params.plan || 'all'}
            onChange={(e) => handlePlanFilter(e.target.value)}
          >
            <option value="all">All Plans</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
          </Select>
          <Select
            value={params.subscriptionStatus || 'all'}
            onChange={(e) => handleStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
            <option value="canceled">Canceled</option>
          </Select>
        </SearchContainer>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatLabel>Total Customers</StatLabel>
          <StatValue>{totalCustomers}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Active Customers</StatLabel>
          <StatValue>{activeCustomers}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Pro Plans</StatLabel>
          <StatValue>{proCustomers}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Starter Plans</StatLabel>
          <StatValue>{starterCustomers}</StatValue>
        </StatCard>
      </StatsContainer>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Customer</TableHeader>
              <TableHeader>Company</TableHeader>
              <TableHeader>Domain</TableHeader>
              <TableHeader>Plan</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Stats</TableHeader>
              <TableHeader>Created</TableHeader>
            </TableRow>
          </TableHead>
          <tbody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState>
                    <div>No customers found</div>
                    <div>Try adjusting your search criteria</div>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer: Customer) => (
                <TableRow key={customer.id} clickable onClick={() => openDetail(customer)}>
                  <TableCell>
                    <div>
                      <div style={{ fontWeight: 600 }}>{customer.name}</div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {customer.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{customer.company}</TableCell>
                  <TableCell>
                    <code style={{
                      background: '#f7fafc',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {customer.domain}
                    </code>
                  </TableCell>
                  <TableCell>
                    <PlanBadge plan={customer.plan}>{customer.plan}</PlanBadge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={customer.subscriptionStatus}>
                      {customer.subscriptionStatus}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '12px', color: '#718096' }}>
                      <div>Users: {customer.stats?.totalUsers || 0}</div>
                      <div>Pages: {customer.stats?.totalPages || 0}</div>
                      <div>Leads: {customer.stats?.totalLeads || 0}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '12px', color: '#718096' }}>
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>

        {pagination && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} customers
            </PaginationInfo>
            <PaginationControls>
              <PaginationButton
                disabled={pagination.currentPage <= 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                Previous
              </PaginationButton>
              <span style={{ color: '#718096', fontSize: '14px' }}>
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <PaginationButton
                disabled={pagination.currentPage >= pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                Next
              </PaginationButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </TableContainer>

      <CreateCustomerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <CustomerDetail
        isOpen={!!selectedCustomerId}
        customer={customerDetailData?.data || null}
        onClose={closeDetail}
        onEdit={(customer) => {
          closeDetail();
          // TODO: Open edit modal with customer data
          console.log('Edit customer:', customer);
        }}
      />
    </Container>
  );
};